<template>
  <div class="frequency-component">

    <header>

      <div>
        <h1>{{ $t('admin.refresh_queries_schedule.title') }}</h1>
      </div>

      <div class="action-wrapper">

        <div class="btn btn-default" :class="[{ disabled: !allowReset }]" @click="reset">
          <i class="fas fa-sync" />
          <span>{{ $t('admin.reset') }}</span>
        </div>

        <div class="btn btn-carma" @click="addRow">
          <i class="fas fa-plus" />
          <span>{{ $t('admin.add') }}</span>
        </div>

        <div class="btn btn-carma" :class="{ disabled: !allowSave }" @click="saveBulk">
          <i class="fas fa-save" />
          <span>{{ $t('admin.save') }}</span>
        </div>

      </div>

    </header>

    <div class="note">
      <div>
        <p>{{ $t('admin.refresh_queries_schedule.note') }}</p></div>
    </div>

    <div>

      <div class="frequency-wrapper" :class="[{ deleted: item.deleted }]" v-for="(item, index) in settingsClone" :key="`freq-${index}`">

        <div class="frequency-select">
          <p>{{ $t('admin.refresh_queries_schedule.frequency') }}: </p>
          <div>
            <input-select
                :selectedValue="item.frequency.value"
                :options="frequencyOptions"
                :preselect-first="true"
                :allow-empty="false"
                @change="(val) => selectFrequency(val, item)"
            />
          </div>
        </div>

        <HoursMinutesSeconds
          :time="item.time"
          :format="item.frequency.format"
          :options="item.frequency"
          :clearable="false"
          @selectTime="(options) => selectTime(options, item)"
        />

        <div class="remove-icon" @click="destroyItem(item)">
          <i class="fas fa-times"/>
        </div>

      </div>

    </div>

  </div>
</template>

<script>
// Plugins
import { mapActions } from "vuex";
import cloneDeep from 'lodash/cloneDeep';

// Components
import InputSelect from '../../form-components/input-select';
import HoursMinutesSeconds from '../../form-components/hour-minute-select/Index.vue'

export default {
  name: "RefreshQueriesSchedule",

  created() {
    this.setupData()
  },

  data() {
    return {
      settingsClone: [],
      allowSave: false,
      allowReset: false,
      frequencyOptions: [
        {
          label: this.$i18n.t('admin.refresh_queries_schedule.daily'),
          value: 'daily',
          format: 'HH:MM', // 'HH:MM:SS'
          default: '00:00:00',
          options: {
            currentLabel: this.$i18n.t('admin.refresh_queries_schedule.run_at')
          }
        },
        {
          label: this.$i18n.t('admin.refresh_queries_schedule.hourly'),
          value: 'hourly',
          format: 'MM',
          default: '00',
          options: {
            currentLabel: this.$i18n.t('admin.refresh_queries_schedule.run_at')
          }
        },
        {
          label: this.$i18n.t('admin.refresh_queries_schedule.at_interval'),
          value: 'at_interval',
          format: 'MM',
          default: '10',
          options: {
            currentLabel: this.$i18n.t('admin.refresh_queries_schedule.run_every'),
            'MM': [10, 20, 30, 40, 45, 60, 90, 120].map(num => ({ label: this.numberToString(num), value: num}))
          }
        }
      ],
    }
  },

  computed: {
    projectId () {  return this.$route.params.project_uid },
    settingsData() { return this.$store.state.queryBuilder.masterView.settings.data },
  },

  methods: {

    ...mapActions({
      'saveSettings': 'queryBuilder/masterView/saveSettings',
    }),

    addRow(bulk = true) {

      const newFrequency = {
        id: null,
        project_id: this.project_id,
        frequency: this.frequencyOptions[0],
        time: { hours: 0, minutes: 0, seconds: 0 }
      }

      if(bulk) {
        this.settingsClone.push(newFrequency)
        this.actionPermissions(true)
      } else {
        this.saveOne(newFrequency)
      }
    },

    destroyItem(item, bulk = true) {
      this.$set(item, 'deleted', true)
      this.actionPermissions(true)
      if(!bulk) this.saveOne(item)
    },

    selectFrequency(val, target, bulk = true) {
      target.frequency = this.frequencyOptions.find(item => item.value === val)
      this.clearTime(target, bulk)
    },

    selectTime(options, target, bulk = true) {
      const { key } = options
      target.time[key] = options[key]
      this.actionPermissions(true)
      if(!bulk) this.saveOne(target)
    },

    clearTime(target, bulk) {
      target.time = {
        hours: 0,
        minutes: target.frequency.value === 'at_interval' ? 10 : 0,
        seconds: 0,
      }
      this.actionPermissions(true)
      if(!bulk) this.saveOne(target)
    },

    //This is to save each schedule individually on change. Not in use at the moment
    saveOne(target) {
      let data = []
      data.push(this.transformFrequency(target))
      this.saveSettings({projectId: this.projectId, data})
      this.actionPermissions(false)
    },

    saveBulk() {
      const data = this.settingsClone.map(item => {
        const format = item.frequency.format.split(':')

        const legalFormat = format.map(f => {
          if(f === 'HH') return 'hours'
          if(f === 'MM') return 'minutes'
          if(f === 'SS') return 'seconds'
        })

        return {
          ...item,
          frequency: item.frequency.value,
          time: legalFormat.map(f => String(item.time[f]).padStart(2, '0')).join(':')
        }
      })

      this.saveSettings({ projectId: this.projectId, data })
      this.actionPermissions(false)
    },

    transformFrequency(target) {
      const format = target.frequency.format.split(':')
      const legalFormat = format.map(f => {
        if(f === 'HH') return 'hours'
        if(f === 'MM') return 'minutes'
        if(f === 'SS') return 'seconds'
      })

      return {
        ...target,
        frequency: target.frequency.value,
        time: legalFormat.map(f => String(target.time[f]).padStart(2, '0')).join(':')
      }
    },

    numberToString(n) {
      let numString = n.toString()
      if(n < 10) numString = `0${n}`
      return numString
    },

    //Sets bool states to enable/disable save and reset buttons
    actionPermissions(state = true) {
      this.allowSave = state
      this.allowReset = state
    },

    reset() {
      this.setupData()
      this.actionPermissions(false)
    },

    setupData() {
      this.settingsClone = this.setSettingsClone()
    },

    setSettingsClone() { // clone settings data in to a data object
      const settingsClone = this.settingsData.map(item => {

        const frequency = this.frequencyOptions.find(option => option.value === item.frequency)
        const timeArray =  item.time ? item.time.split(':').map(num => parseInt(num)) : frequency.default
        const itemFrequencyFormat = frequency.format.split(':') || []

        const time = itemFrequencyFormat.reduce((acc, key, index) => {

          let timeKey = 'hours'
          if(key === 'MM') timeKey = 'minutes'
          if(key === 'SS') timeKey = 'seconds'

          acc[timeKey] = timeArray[index] || 0
          return acc
        }, {})

        return {
          ...item,
          ...{frequency},
          ...{time}
        }
      });

      return cloneDeep(settingsClone)
    }
  },
  watch: {
    settingsData: { // when store data change, update local data too
      handler() {
        this.setupData()
      }, deep: true
    }
  },
  components: {
    InputSelect,
    HoursMinutesSeconds
  }
}
</script>

<style lang="scss">

@import '../../../styles/base/variables.scss';

.frequency-component {

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
    border-bottom: none;

    h1 {
      margin-right: 1.2rem;
      margin-bottom: 0;
    }

    .btn {
      display: inline-block;
      width: auto;
      font-size: 13px;
      padding: 10px 14px;
      margin-right: .4rem;
      line-height: 1.1;
      height: auto;
      border-radius: 0;

      i {
        margin-right: 0.4rem;
        font-size: 1rem;
      }
    }

    .add-icon {
      cursor: pointer;
      font-size: 1.2rem;
      color: $green-dark;

      &:hover {
        color: $blue;
      }
    }
  }

  .action-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-grow: 1;
  }

  .note {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 2rem;
    color: $light-text;
    font-style: italic;
  }
}

.frequency-wrapper {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;

  &.deleted {
    display: none;
  }

  p {
    margin-bottom: 0;
  }

  .remove-icon {
    padding: 0.3rem .5rem .3rem 1rem;
    cursor: pointer;
    font-size: 1.3rem;
    color: $light-text;
    transition: color 0.3s;

    &:hover {
      color: $red;
    }
  }
}

.frequency-select {
  display: flex;
  align-items: center;
  margin-right: 2rem;

   p {
     margin-right: 1rem;
   }

  .input-select.multiselect {

    .multiselect__select {
      height: 30px;
    }

    .clear-select {
      height: 27px;
      display: flex;
      align-items: center;
    }

    div.multiselect__tags {
      display: flex;
      align-items: center;
      padding-top: 0;
      padding-bottom: 0;
      background-color: $off-white;
    }
  }
}
</style>